"use client";

import React, { useState, useEffect } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Card } from "@/components/ui/card";
import { useTranslation } from "next-i18next";
import { useSetRecoilState } from "recoil";
import { guideModalAtom } from "@/Atoms/modal/DialogueAtoms";
import { useRouter } from "next/router";

export function SidebarCarousel() {
  const router = useRouter();
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [approachModalOpen, setApproachModalOpen] = useState(false);

  const { t } = useTranslation("layout-page");
  const setGuideModal = useSetRecoilState(guideModalAtom);

  const slides: {
    type?: "text" | "banner";
    title?: string;
    description?: React.ReactNode;
    bannerUrl?: string;
    linkUrl?: string;
    onClick?: () => void;
  }[] = [
    {
      type: "banner",
      bannerUrl: "/images/scanner.png",
      onClick: () => router.push("/test?locale=en&tab=scanners"),
    },
  ];
  // track current index and total count
  useEffect(() => {
    if (!api) return;
    const update = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
    };
    update();
    api.on("select", update);
    return () => {
      api.off("select", update);
    };
  }, [api]);

  // auto-slide every 3 seconds
  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      api.scrollNext(); // goes to next slide (loop enabled)
    }, 7000);
    return () => clearInterval(interval);
  }, [api]);

  const goTo = (idx: number) => {
    api?.scrollTo(idx);
  };

  return (
    <div className="relative w-full max-w-md mx-auto">
      <Carousel
        setApi={setApi}
        opts={{ loop: true, align: "center" }}
        className="w-full "
      >
        <CarouselContent>
          {slides.map((slide, idx) => (
            <CarouselItem
              key={idx}
              className="flex justify-center items-center"
            >
              <div className="p-0 w-full select-none">
                {slide.type === "banner" ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      if (slide.onClick) {
                        slide.onClick();
                      } else if (slide.linkUrl) {
                        setGuideModal({ isOpen: true, link: slide.linkUrl });
                      }
                    }}
                    className="block w-full h-[100px] outline-none border-2 border-white/50 rounded-xl "
                  >
                    <img
                      src={slide.bannerUrl}
                      alt="Banner"
                      className="w-full h-full object-cover rounded-xl"
                    />
                  </button>
                ) : (
                  <Card className="bg-muted-foreground/15 h-[100px] p-2 text-sm flex flex-col gap-2 w-full pb-6">
                    <p className="font-bold">{slide.title}</p>
                    <p className="text-xs font-bold text-muted-foreground">
                      {slide.description}
                    </p>
                  </Card>
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Dots navigation */}
      <div className="flex absolute bottom-2 left-4 justify-center space-x-2 hidden">
        {Array.from({ length: count }).map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`w-3 h-3 rounded-full ${
              idx === current ? "bg-muted-foreground" : "bg-muted-foreground/30"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
